import React, { useState, useEffect, useRef } from 'react';
import imageCache from '../utils/imageCache';

/**
 * CachedImage Component
 * Automatically caches images to prevent reloading when navigating
 */
const CachedImage = ({
  src,
  alt,
  className = '',
  fallbackSrc = null,
  placeholder = null,
  onLoad = () => {},
  onError = () => {},
  loading = 'lazy',
  preload = false,
  cacheOptions = {},
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder || '');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isVisible, setIsVisible] = useState(!loading || loading !== 'lazy');
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading === 'lazy' && imgRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              observerRef.current?.disconnect();
            }
          });
        },
        { threshold: 0.1, rootMargin: '50px' }
      );

      observerRef.current.observe(imgRef.current);

      return () => {
        observerRef.current?.disconnect();
      };
    }
  }, [loading]);

  // Load image when visible
  useEffect(() => {
    if (!src || !isVisible) return;

    const loadImage = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        // Check if image is already cached
        const cachedUrl = imageCache.getCached(src, cacheOptions);
        if (cachedUrl) {
          setImageSrc(cachedUrl);
          setIsLoading(false);
          onLoad();
          return;
        }

        // Load and cache the image
        const loadedUrl = await imageCache.loadImage(src, cacheOptions);
        setImageSrc(loadedUrl);
        setIsLoading(false);
        onLoad();
      } catch (error) {
        console.warn('Error loading cached image:', error);
        handleImageError();
      }
    };

    loadImage();
  }, [src, isVisible, cacheOptions, onLoad]);

  // Preload image if requested
  useEffect(() => {
    if (preload && src) {
      imageCache.loadImage(src, cacheOptions).catch(() => {
        // Silently handle preload errors
      });
    }
  }, [src, preload, cacheOptions]);

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
    
    if (fallbackSrc && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
    } else {
      onError();
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    onLoad();
  };

  // Show placeholder while loading
  if (!isVisible || (isLoading && placeholder)) {
    return (
      <div
        ref={imgRef}
        className={`${className} bg-gray-200 animate-pulse flex items-center justify-center`}
        {...props}
      >
        {placeholder || (
          <div className="text-gray-400 text-sm">Loading...</div>
        )}
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      onLoad={handleImageLoad}
      onError={handleImageError}
      loading={loading === 'lazy' ? 'lazy' : 'eager'}
      {...props}
    />
  );
};

export default CachedImage;
